/* oxlint-disable typescript/no-explicit-any -- router core with generic route types */
import type {
  AnyRoute,
  RouterContextRecord,
  RouterInstance,
  RouterTypesRecord,
  RouterNavigateFn,
  RouterBuildLocationFn,
  RouterMatchRouteFn,
  RouteByFullPath,
  ParamsRecord,
  SearchRecord,
  NavigateOptionsForTree,
  ParamsFromPath,
} from './types'
import {
  buildRouterMetadata,
  fillParams,
  buildSearch,
  parseSearchParams,
  matchPathnameRuntime,
  getRouteChain,
} from './utils'
import { createRouteOptionsApi } from './route-options'
import { getDefaults } from '@w/schema'
import { getNavigation } from './navigation-polyfill'
import type { NavigationLike } from './navigation-polyfill'
import { createRouteTel, nullTel, type RootTel, type Tel } from '@w/tel'

/** A card is the target route's params, optionally pinning its search (§4.5). */
export type QueueCardFor<TTo extends string> = ParamsFromPath<TTo> & {
  search?: SearchRecord
}

export class RedirectError<
  TOptions extends Record<string, unknown>,
> extends Error {
  constructor(public readonly options: TOptions) {
    super('Redirect')
  }
}

type RedirectParamsRequirement<TTo extends string> = string extends TTo
  ? { params?: ParamsRecord }
  : keyof ParamsFromPath<TTo> extends never
    ? { params?: undefined }
    : { params: ParamsFromPath<TTo> }

type RedirectBase<TTo extends string> = {
  to: TTo
  search?: SearchRecord
  hash?: string
  replace?: boolean
  /** Entry state (§5) seeded onto the destination's history entry. */
  state?: unknown
}

/**
 * Queue birth (QUEUE.md §4.2). `params` is absent by design — the queue
 * supplies them, and `to` is still named exactly once.
 */
type RedirectQueueOptions<TTo extends string> = RedirectBase<TTo> & {
  queue: QueueCardFor<TTo>[]
  queueState?: unknown
  prefetch?: number
  /** Deck identity when one route carries several decks (§6). */
  queueId?: string
  params?: undefined
}

type RedirectOptions<TTo extends string> =
  | (RedirectBase<TTo> & RedirectParamsRequirement<TTo> & { queue?: undefined })
  | RedirectQueueOptions<TTo>

export const redirect = <const TTo extends string>(
  options: RedirectOptions<TTo>,
) => new RedirectError(options)

export const isRedirect = (
  value: unknown,
): value is RedirectError<Record<string, unknown>> =>
  value instanceof RedirectError

const registeredRouterTypeSymbol = Symbol('registeredRouterType')

type RouterTypeMetadata<
  TRouteTree extends AnyRoute,
  TRouterContext extends RouterContextRecord,
> = RouterTypesRecord<TRouteTree, TRouterContext>

export type RegisteredRouter<
  TRouteTree extends AnyRoute,
  TRouterContext extends RouterContextRecord,
> = RouterInstance<TRouteTree, TRouterContext> & {
  readonly [registeredRouterTypeSymbol]: RouterTypeMetadata<
    TRouteTree,
    TRouterContext
  >
}

export type AnyRegisteredRouter = RegisteredRouter<
  AnyRoute,
  RouterContextRecord
>

function attachRouterTypeMetadata<
  TRouteTree extends AnyRoute,
  TRouterContext extends RouterContextRecord,
>(
  router: RouterInstance<TRouteTree, TRouterContext>,
): RegisteredRouter<TRouteTree, TRouterContext> {
  if (!(registeredRouterTypeSymbol in router)) {
    Object.defineProperty(router, registeredRouterTypeSymbol, {
      value: {
        routeTree: router.routeTree,
        ctx: router.ctx,
        routePaths: router.types.routePaths,
        routesById: router.types.routesById,
        routesByToPath: router.types.routesByToPath,
        navigateOptionsByToPath: router.types.navigateOptionsByToPath,
      },
      enumerable: false,
      configurable: false,
      writable: false,
    })
  }
  return router as RegisteredRouter<TRouteTree, TRouterContext>
}

type EnsureRootRoute<TRoute extends AnyRoute> =
  TRoute['types']['parentRoute'] extends null ? TRoute : never

export interface RouterOptions<
  TRouteTree extends AnyRoute,
  TRouterContext extends RouterContextRecord,
> {
  routeTree: EnsureRootRoute<TRouteTree>
  ctx: TRouterContext
  navigation?: NavigationLike
  tel?: RootTel
}

const ensureRoute = <T extends AnyRoute>(route: T) => route

export type MatchedRoute = {
  route: AnyRoute
  params: ParamsRecord
  search: SearchRecord
  exactMatch: boolean
}

export type RouteMatch = {
  matches: MatchedRoute[]
  notFound: boolean
  search: SearchRecord
}

export type Location = {
  pathname: string
  search: string
}

/** The navigation's current entry as a router location; defaults to the global Navigation API (native installs the memory polyfill). */
export function currentLocation(navigation: NavigationLike = getNavigation()): Location {
  const url = new URL(navigation.currentEntry.url)
  return { pathname: url.pathname, search: url.search }
}

// Pure function to match routes against a location
export function matchRoutes(
  location: Location,
  routeTree: AnyRoute,
  metadata: ReturnType<typeof buildRouterMetadata>,
): RouteMatch {
  const pathname = location.pathname || '/'
  const normalizedPathname = pathname.startsWith('/')
    ? pathname
    : `/${pathname}`
  const searchRecord = parseSearchParams(location.search)

  let matchedRoute: AnyRoute | null = null
  let matchedByPath = false
  for (const [pattern, candidateRoute] of metadata.rankedRoutes) {
    const paramsMatch = matchPathnameRuntime(pattern, normalizedPathname)
    if (paramsMatch !== null) {
      matchedRoute = candidateRoute
      if (candidateRoute !== routeTree) {
        matchedByPath = true
      } else if (normalizedPathname === '/') {
        matchedByPath = true
      }
      break
    }
  }

  matchedRoute ??= routeTree

  const isNotFound = matchedRoute === routeTree && !matchedByPath

  const chain = getRouteChain(matchedRoute)
  if (chain.length === 0) {
    chain.unshift(routeTree)
  }

  // oxlint-disable-next-line oxc/no-accumulating-spread -- chain length bounded by route depth; O(n^2) is not a concern
  const accumulatedParams: ParamsRecord = {}
  const matches: MatchedRoute[] = []

  for (const route of chain) {
    const paramsMatch = matchPathnameRuntime(route.fullPath, normalizedPathname)
    if (paramsMatch) {
      Object.assign(accumulatedParams, paramsMatch)
    }
    const routeSearch = applySearchSchema(route, searchRecord)
    matches.push({
      route,
      params: { ...accumulatedParams },
      search: routeSearch,
      exactMatch: isExactRouteMatch(route, normalizedPathname),
    })
  }

  return {
    matches,
    notFound: isNotFound,
    search: searchRecord,
  }
}

function isExactRouteMatch(route: AnyRoute, pathname: string): boolean {
  return matchPathnameRuntime(route.fullPath, pathname) !== null
}

function applySearchSchema(route: AnyRoute, base: SearchRecord): SearchRecord {
  const options = route.options as any
  if (!options?.searchSchema) {
    return { ...base }
  }
  const schema = options.searchSchema as
    | { parse?: (value: SearchRecord) => SearchRecord }
    | ((value: SearchRecord) => SearchRecord)
  try {
    if (
      schema &&
      typeof schema === 'object' &&
      'parse' in schema &&
      typeof schema.parse === 'function'
    ) {
      return schema.parse({ ...base })
    }
    if (typeof schema === 'function') {
      return schema({ ...base })
    }
  } catch {
    return { ...base }
  }
  return { ...base }
}

const runSchema = (schema: unknown, value: unknown): unknown => {
  if (
    schema &&
    typeof schema === 'object' &&
    'parse' in schema &&
    typeof schema.parse === 'function'
  ) {
    return (schema as { parse: (input: unknown) => unknown }).parse(value)
  }
  if (typeof schema === 'function') {
    return (schema as (input: unknown) => unknown)(value)
  }
  return value
}

/**
 * `parse` does not materialize declared defaults in this schema library —
 * `getDefaults` does. §5 promises "required defaults => the read type is always
 * T", so the defaults are layered UNDER the stored state before parsing.
 * Non-object schemas have no defaults to derive; they get an empty layer.
 */
const schemaDefaults = (schema: unknown): Record<string, unknown> => {
  try {
    return getDefaults(schema as never) ?? {}
  } catch {
    return {}
  }
}

/**
 * Entry state, defensively parsed (§5). Stale or corrupt state — written by an
 * older deploy, or restored from a session history file — falls back to the
 * schema's defaults, never throws. Without a schema the raw value passes
 * through, so a route that declares nothing still reads what it wrote.
 */
export function applyStateSchema(route: AnyRoute, raw: unknown): unknown {
  const schema = (route.options as { stateSchema?: unknown } | undefined)
    ?.stateSchema
  if (!schema) return raw

  const defaults = schemaDefaults(schema)
  const seeded =
    typeof raw === 'object' && raw !== null && !Array.isArray(raw)
      ? { ...defaults, ...raw }
      : (raw ?? defaults)
  try {
    return runSchema(schema, seeded)
  } catch {
    try {
      return runSchema(schema, defaults)
    } catch {
      return defaults
    }
  }
}

export const createRouter = <
  TRouteTree extends AnyRoute,
  TRouterContext extends RouterContextRecord,
>(
  options: RouterOptions<TRouteTree, TRouterContext>,
) => {
  const routeTree = ensureRoute(options.routeTree)
  const metadata = buildRouterMetadata(routeTree)
  const types: RouterTypesRecord<TRouteTree, TRouterContext> = {
    routeTree,
    ctx: options.ctx,
    routePaths: metadata.routePaths,
    routesById: metadata.routesById,
    routesByToPath: metadata.routesByToPath,
    navigateOptionsByToPath: metadata.navigateOptionsByToPath,
  }
  let currentTel: Tel = options.tel ?? nullTel

  const startNavigation = (pathname: string) => {
    if (!options.tel) return
    currentTel = createRouteTel(options.tel, currentTel, pathname)
  }

  const runRouteHook = async <T>(input: {
    match: MatchedRoute
    name: 'route.beforeLoad' | 'route.loader'
    preload: boolean
    tel: Tel
    hook: (args: Record<string, unknown>) => T
  }): Promise<Awaited<T>> => {
    const result: { value?: Awaited<T>; redirect?: Error } = {}
    await input.tel.span(
      {
        name: input.name,
        displayName: input.match.route.fullPath ?? input.match.route.id,
        target: 'router',
        input: { params: input.match.params, search: input.match.search },
        attributes: input.preload ? { 'router.preload': true } : undefined,
      },
      async (tel) => {
        try {
          result.value = await input.hook({
            route: createRouteOptionsApi(
              input.match.route,
              input.match.params,
              input.match.search,
              input.match.exactMatch,
            ),
            params: input.match.params,
            search: input.match.search,
            ctx: options.ctx,
            tel,
          })
        } catch (error) {
          if (!isRedirect(error)) throw error
          result.redirect = error
        }
        return null
      },
    )
    if (result.redirect) throw result.redirect
    return result.value as Awaited<T>
  }

  const buildLocation: RouterBuildLocationFn<TRouteTree> = (navigate) => {
    const params = (navigate as { params?: ParamsRecord }).params
    const pathname = fillParams(navigate.to, params)
    const search =
      'search' in navigate
        ? buildSearch((navigate as { search?: SearchRecord }).search)
        : ''
    const hash = navigate.hash ? `#${navigate.hash}` : ''
    return `${pathname}${search}${hash}`
  }

  const navigate: RouterNavigateFn<TRouteTree> = (opts) => {
    const path = buildLocation(opts)
    const navigation = options.navigation ?? getNavigation()
    navigation.navigate(path, {
      history: opts.replace ? 'replace' : 'push',
      state: (opts as { state?: unknown }).state,
    })
  }

  const matchRoute: RouterMatchRouteFn<TRouteTree> = ({ to }) => {
    const route = (
      metadata.routesByToPath as Record<string, AnyRoute | undefined>
    )[to as string]
    if (!route) return
    const currentMatch = matchRoutes(currentLocation(options.navigation ?? getNavigation()), routeTree, metadata)
    const matched = currentMatch.matches.find((m) => m.route.id === route.id)
    // oxlint-disable-next-line typescript/consistent-return -- mixed return patterns required by no-useless-undefined constraint
    return matched
      ? (route as RouteByFullPath<TRouteTree, typeof to>)
      : undefined
  }

  const preload = async (
    opts: NavigateOptionsForTree<TRouteTree>,
  ): Promise<void> => {
    const path = buildLocation(opts)
    const [pathname, searchStr] = path.split('?')
    const location = { pathname, search: searchStr ? `?${searchStr}` : '' }
    const matched = matchRoutes(location, routeTree, metadata)
    const preloadTel = currentTel

    for (const match of matched.matches) {
      const beforeLoad = (match.route.options as any)?.beforeLoad
      if (!beforeLoad) continue

      try {
        await runRouteHook({
          match,
          name: 'route.beforeLoad',
          preload: true,
          tel: preloadTel,
          hook: beforeLoad,
        })
      } catch {
        return
      }
    }

    // Run loaders in background without updating navigation
    const promises = matched.matches.map((match) => {
      const loader = (match.route.options as any)?.loader
      if (!loader) return Promise.resolve()

      return Promise.resolve()
        .then(() =>
          runRouteHook({
            match,
            name: 'route.loader',
            preload: true,
            tel: preloadTel,
            hook: loader,
          }),
        )
        .catch(() => {
          // Swallow errors during preload - they'll be thrown again on actual navigation
        })
    })

    await Promise.all(promises)
  }

  const router = {
    routeTree,
    ctx: options.ctx,
    types,
    metadata,
    navigation: options.navigation,
    navigate,
    buildLocation,
    matchRoute,
    preload,
    get currentTel() {
      return currentTel
    },
    _startNavigation: startNavigation,
  } satisfies RouterInstance<TRouteTree, TRouterContext>

  return attachRouterTypeMetadata(router)
}
